import { Box, Divider, Typography } from '@mui/material';
import { ReactComponent as VerticalDotsIcon } from 'assets/vertical-dots-white.svg';
import ClientAvatar from 'components/shared/ClientAvatar';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import StageChip from 'components/shared/StageChip';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import React from 'react';
import { Link } from 'react-router-dom';
import RoutePaths from 'routes/RoutePaths';
import { DealsService } from 'service/deals.service';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setStage } from 'store/slices/dealsSlice';
import zeplinColor from 'theme/Color';
import DealDropdown from '../../DealDropdown';
import ActionsMenu from '../ActionsMenu';
import { muiStyles } from './muiStyles';
import styles from './styles.module.css';
import { useActionsMenu } from './useActionsMenu';
import { getMetaData } from './utils';

const MetaData = () => {
  const dispatch = useAppDispatch();
  const deal = useAppSelector((state) => state.deals.deal);
  const { stages, stage } = useAppSelector((state) => state.deals);

  const { dealId, amount, client, business, stageName } = deal;
  const data = getMetaData(stageName, amount, client?.clientName, business?.name);

  const { alertState, setAlertState, getAlertContent, startOperation, completeOperation } =
    useAlertStatus();

  const { anchorRef, handleCloseActionsMenu, handleOpenActionsMenu } = useActionsMenu();

  const handleFieldChange = async (fieldName: string, newValue: string) => {
    if (!dealId) return;

    try {
      startOperation('update', 'Updating deal...');
      await DealsService.updateDeal(dealId, { [fieldName]: newValue });
      completeOperation('update', 'Deal updated successfully.');
    } catch (error: any) {
      completeOperation('update', error?.response?.data?.message || 'Failed to update deal', true);
    }
  };

  const handleStageChange = (fieldName: string, value: string | null) => {
    if (value === null) return;

    dispatch(setStage(value));
    handleFieldChange(fieldName, value);
  };

  return (
    <>
      <Box sx={muiStyles.root}>
        {data.map(({ label, value, type }) => (
          <React.Fragment key={label}>
            <Box sx={muiStyles.item}>
              {type === 'chip' && <StageChip dealType={value ?? ''} />}

              {type === 'select' && (
                <DealDropdown
                  options={stages}
                  value={stage}
                  setValue={handleStageChange}
                  fieldName='stageName'
                  isBgDark
                  isChip
                />
              )}

              {type === 'avatar' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ClientAvatar
                    clientName={client?.clientName}
                    profilePicture={client?.avatar}
                    sx={{
                      width: 24,
                      height: 24,
                    }}
                  />
                  <Link to={`${RoutePaths.HuddleClient}/${client?.accountId}`}>
                    <Typography
                      variant='responsiveHeading5'
                      sx={{ color: zeplinColor.Neutral1, textDecoration: 'underline' }}
                    >
                      {value}
                    </Typography>
                  </Link>
                </Box>
              )}

              {type === 'business' && (
                <Link to={`${RoutePaths.Businesses}/${business?.businessId}`}>
                  <Typography
                    variant='responsiveHeading5'
                    sx={{ color: zeplinColor.Neutral1, textDecoration: 'underline' }}
                  >
                    {value}
                  </Typography>
                </Link>
              )}

              {type === 'text' && (
                <Typography variant='responsiveHeading5' sx={{ color: zeplinColor.Neutral1 }}>
                  {value}
                </Typography>
              )}

              {type === 'amount' && (
                <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.Neutral1 }}>
                  {value}
                </Typography>
              )}

              {type === 'actions' && (
                <Box
                  sx={{
                    padding: '3px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    ':hover': {
                      backgroundColor: zeplinColor.Neutral9,
                      cursor: 'pointer',
                    },
                  }}
                >
                  <VerticalDotsIcon
                    className={styles['vertical-icon']}
                    onClick={handleOpenActionsMenu}
                  />

                  <ActionsMenu
                    onClose={handleCloseActionsMenu}
                    anchor={anchorRef.current}
                    startOperation={startOperation}
                    completeOperation={completeOperation}
                  />
                </Box>
              )}

              {label && (
                <Typography variant='responsiveBody3' sx={{ color: zeplinColor.Neutral5 }}>
                  {label}
                </Typography>
              )}
            </Box>
            <Divider sx={muiStyles.divider} />
          </React.Fragment>
        ))}
      </Box>

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </>
  );
};

export default MetaData;
